import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, MapPin, Calendar, Package, Leaf, Workflow,
  CheckCircle, AlertCircle, Inbox, Nfc, Lock, Loader2,
} from 'lucide-react';
import OwnershipFooter from '../components/common/OwnershipFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public QR Verification — customer-facing experience.
 *
 * Intentionally simplified per platform spec:
 *  - Shows product identity, origin, traceability + sustainability + compliance status
 *  - DOES NOT show internal actors, system sources, audit logs, or correction forms
 *  - Empty states are honest and customer-friendly
 */
const VerifyBatch = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const viaNfc = searchParams.get('via') === 'nfc';
  const [batch, setBatch] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // C1+C2 — Trust panel state. Always fetched (passive); the explicit
  // "Verify Integrity" button re-fetches and animates.
  const [integrity, setIntegrity] = useState(null);
  const [integrityChecking, setIntegrityChecking] = useState(false);

  const fetchIntegrity = async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/audit-chain/verify?batch_id=${id}`,
      );
      setIntegrity(r.data);
    } catch {
      setIntegrity(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [b, e] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/batches/${id}`).catch(() => null),
          axios.get(`${BACKEND_URL}/api/batches/${id}/events`).catch(() => ({ data: [] })),
        ]);
        setBatch(b?.data || null);
        setEvents(e?.data || []);
        await fetchIntegrity();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleVerifyIntegrity = async () => {
    setIntegrityChecking(true);
    // Small artificial delay so the user actually perceives the verify
    // step happening — purely UX, the API call itself returns in <100ms.
    await new Promise((res) => setTimeout(res, 700));
    await fetchIntegrity();
    setIntegrityChecking(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  const hasBatch = !!batch;
  const hasEvents = events.length > 0;
  const hasSustainability = batch?.total_co2_kg != null;
  const hasCompliance = Array.isArray(batch?.compliance) && batch.compliance.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="public-verify-page">
      {/* Header band */}
      <header className="bg-emerald-700 text-white py-8 sm:py-10 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 flex-wrap mb-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full backdrop-blur-sm">
              <ShieldCheck className="w-4 h-4" />
              <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider">Verified by PureSustain</span>
            </div>
            {viaNfc && (
              <div
                data-testid="nfc-verified-badge"
                className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/20 border border-cyan-300/40 rounded-full backdrop-blur-sm"
              >
                <Nfc className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider">NFC Verified</span>
              </div>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
            {hasBatch ? batch.product_name : 'Product Not Found'}
          </h1>
          <p className="text-xs sm:text-sm text-emerald-100 italic">
            {hasBatch ? batch.species : `Batch ID: ${id}`}
          </p>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-10">
        <div className="max-w-3xl mx-auto space-y-5 sm:space-y-6">
          {!hasBatch ? (
            <NotFoundCard id={id} />
          ) : (
            <>
              <ProductIdentityCard batch={batch} />
              <TraceabilitySection hasEvents={hasEvents} />
              <TrustPanel
                integrity={integrity}
                checking={integrityChecking}
                onVerify={handleVerifyIntegrity}
              />
              <SustainabilitySection batch={batch} available={hasSustainability} />
              <ComplianceSection batch={batch} available={hasCompliance} />
            </>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed">
              This verification page is generated automatically from records
              persisted within PureSustain. It supports transparency to
              consumers, retailers, auditors, partners, and buyers — but does
              not replace regulatory filings or accredited certification.
            </p>
          </div>
        </div>
      </main>

      <OwnershipFooter />
    </div>
  );
};

/* ----------------------- sections ----------------------- */

const NotFoundCard = ({ id }) => (
  <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 sm:p-12 text-center">
    <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-4">
      <Inbox className="w-7 h-7 text-slate-400" />
    </div>
    <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
      Batch <span className="font-mono">{id}</span> not yet registered
    </h2>
    <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
      This QR code points to a product batch that has not yet been registered
      in PureSustain. Once operational records are recorded, full
      verification details will appear here.
    </p>
  </div>
);

const ProductIdentityCard = ({ batch }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
    <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4" style={{ fontFamily: 'Manrope' }}>
      Product Details
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      <PublicField icon={Package} label="Batch ID" value={batch.batch_number || batch.id} />
      <PublicField icon={Package} label="Species" value={batch.species} />
      <PublicField icon={MapPin} label="Origin" value={batch.origin?.name} sub={batch.origin?.region} />
      <PublicField icon={MapPin} label="Catch / Harvest" value={batch.origin?.name} />
      <PublicField icon={Calendar} label="Production Date" value={formatDate(batch.catch_date)} />
      <PublicField icon={Calendar} label="Timestamp" value={formatTime(batch.catch_date)} />
      <PublicField icon={Workflow} label="Current Status" value={batch.current_status} />
    </div>
  </div>
);

const TraceabilitySection = ({ hasEvents }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="public-traceability-section">
    <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
      <Workflow className="w-5 h-5 text-emerald-600" />
      Traceability Verification
    </h2>
    {hasEvents ? (
      <>
        <div className="flex flex-wrap gap-2 mb-3">
          <VerifiedBadge text="Traceability Verified" />
          <VerifiedBadge text="Audit Ready" />
        </div>
        <p className="text-xs sm:text-sm text-slate-700">
          Supply-chain events for this batch have been recorded and verified
          within PureSustain.
        </p>
      </>
    ) : (
      <EmptyInline message="Traceability records have not yet been registered for this batch." />
    )}
  </div>
);

const SustainabilitySection = ({ batch, available }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="public-sustainability-section">
    <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
      <Leaf className="w-5 h-5 text-emerald-600" />
      Sustainability Summary
    </h2>
    {available ? (
      <div className="grid grid-cols-2 gap-3">
        <PublicField icon={Leaf} label="Status" value="Recorded" />
        <PublicField icon={Leaf} label="Total CO₂" value={`${batch.total_co2_kg} kg`} />
      </div>
    ) : (
      <EmptyInline message="No sustainability records available yet." />
    )}
  </div>
);

const ComplianceSection = ({ batch, available }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="public-compliance-section">
    <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
      <ShieldCheck className="w-5 h-5 text-emerald-600" />
      Compliance Summary
    </h2>
    {available ? (
      <div className="space-y-2">
        <p className="text-xs sm:text-sm text-slate-700">
          This batch&apos;s compliance records have been documented within
          PureSustain. Verified standards:
        </p>
        <div className="flex flex-wrap gap-2 mt-2">
          {batch.compliance.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] sm:text-xs font-medium text-emerald-800">
              <CheckCircle className="w-3 h-3" /> {c.standard}
            </span>
          ))}
        </div>
      </div>
    ) : (
      <EmptyInline message="No compliance records available yet." />
    )}
  </div>
);

const PublicField = ({ icon: Icon, label, value, sub }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1.5 text-slate-500 mb-1">
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="text-[10px] sm:text-xs uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-sm sm:text-base font-medium text-slate-900 break-words">{value || '—'}</p>
    {sub && <p className="text-[11px] sm:text-xs text-slate-500 truncate">{sub}</p>}
  </div>
);

const VerifiedBadge = ({ text }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] sm:text-xs font-semibold text-emerald-800">
    <CheckCircle className="w-3.5 h-3.5" /> {text}
  </span>
);

const EmptyInline = ({ message }) => (
  <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3 sm:p-4">
    <div className="flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
      <p className="text-xs sm:text-sm text-slate-500">{message}</p>
    </div>
  </div>
);

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-NO', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const formatTime = (d) => (d ? new Date(d).toLocaleString() : '—');
const TrustPanel = ({ integrity, checking, onVerify }) => {
  // Consumer-facing panel — governance-positioning, no operator identities,
  // no internal notes, no corrective-action wording.
  const verified = integrity?.status === 'OK';
  const under_review = integrity && integrity.status && integrity.status !== 'OK' && integrity.status !== 'EMPTY';
  const empty = !integrity || integrity.status === 'EMPTY';
  const live = integrity?.live_events ?? 0;
  const corrected = integrity?.manually_resolved ?? 0;
  const auditPct = integrity?.audit_integrity_pct;
  const anchor = integrity?.latest_anchor;
  const anchorDate = anchor?.anchored_at ? new Date(anchor.anchored_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  }) : null;
  const anchorPublicUri = (anchor?.witnesses || []).find((w) => w.type === 'public_url')?.uri;

  return (
    <div
      data-testid="public-verify-trust-panel"
      data-integrity-status={integrity?.status || 'UNKNOWN'}
      className={`rounded-xl border p-4 sm:p-6 ${
        verified
          ? 'bg-emerald-50 border-emerald-200'
          : under_review
            ? 'bg-amber-50 border-amber-200'
            : 'bg-white border-slate-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-md flex-shrink-0 ${
            verified ? 'bg-emerald-100 text-emerald-700'
            : under_review ? 'bg-amber-100 text-amber-700'
            : 'bg-slate-100 text-slate-500'
          }`}
        >
          <Lock className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2
            className="text-base sm:text-lg font-semibold leading-tight"
            style={{ fontFamily: 'Manrope' }}
          >
            {verified
              ? 'Integrity Verified'
              : under_review
                ? 'Under Review'
                : 'Verifiable Trust'}
          </h2>
          <p className="mt-1 text-[12px] sm:text-sm text-slate-600 leading-snug">
            PureSustain records every event in a tamper-evident audit chain
            and publishes an independent integrity record. You can verify
            this batch yourself, any time.
          </p>
        </div>
      </div>

      {/* Stats strip — three at-a-glance trust numbers. */}
      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
        <div
          data-testid="public-verify-trust-live"
          className="bg-white rounded-lg border border-slate-200 px-2 py-2 sm:py-3 text-center"
        >
          <p className="text-base sm:text-xl font-bold text-emerald-700 tabular-nums">{empty ? '—' : live}</p>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 leading-tight">Live-captured events</p>
        </div>
        <div
          data-testid="public-verify-trust-resolved"
          className="bg-white rounded-lg border border-slate-200 px-2 py-2 sm:py-3 text-center"
        >
          <p className="text-base sm:text-xl font-bold text-amber-700 tabular-nums">{empty ? '—' : corrected}</p>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 leading-tight">Verified operator resolutions</p>
        </div>
        <div
          data-testid="public-verify-trust-integrity"
          className="bg-white rounded-lg border border-slate-200 px-2 py-2 sm:py-3 text-center"
        >
          <p className="text-base sm:text-xl font-bold text-slate-800 tabular-nums">
            {empty || auditPct === null || auditPct === undefined ? '—' : `${auditPct.toFixed(1)}%`}
          </p>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 leading-tight">Audit Integrity</p>
        </div>
      </div>

      {/* Anchor line — visible whenever any anchor exists. */}
      {anchor && (
        <div
          data-testid="public-verify-trust-anchor"
          className="mt-4 rounded-md border border-slate-200 bg-white/80 px-3 py-2 text-[11px] sm:text-xs leading-snug"
        >
          <p className="text-slate-700">
            Verified by independent integrity record dated{' '}
            <span className="font-semibold">{anchorDate}</span>.
          </p>
          {anchorPublicUri && (
            <a
              href={`${BACKEND_URL}${anchorPublicUri}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-1 text-emerald-700 font-medium underline decoration-emerald-300 hover:decoration-emerald-600"
              data-testid="public-verify-trust-anchor-link"
            >
              View public anchor →
            </a>
          )}
        </div>
      )}

      {/* Verify Integrity CTA + result */}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          type="button"
          onClick={onVerify}
          disabled={checking}
          data-testid="public-verify-integrity-button"
          className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm ${
            verified
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : under_review
                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                : 'bg-slate-900 hover:bg-slate-800 text-white'
          } ${checking ? 'opacity-75 cursor-wait' : ''}`}
        >
          {checking ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying&hellip;
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" />
              Verify Integrity
            </>
          )}
        </button>

        {!checking && verified && (
          <div
            data-testid="public-verify-integrity-result"
            data-state="verified"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-semibold"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            All recorded events form an unbroken audit chain.
          </div>
        )}
        {!checking && under_review && (
          <div
            data-testid="public-verify-integrity-result"
            data-state="under-review"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-xs font-semibold"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            Under review &mdash; our compliance team is investigating.
          </div>
        )}
      </div>
    </div>
  );
};


export default VerifyBatch;
